import { apiClient } from '@/lib/api'

export interface QuoteStandard {
  id: string
  tenantId: string
  category: string
  title: string
  preferredVendor: string | null
  minSpecsJson: unknown
  defaultServiceItemCodes: unknown
  sapPreferredGroupCodes: unknown
  sourceType: 'confluence' | 'manual' | 'excel'
  confluencePageId: string | null
  confluenceSpaceKey: string | null
  sourceUrl: string | null
  bodyMd: string | null
  lastSyncedAt: string | null
  /** NULL = globally visible to all tenants ("Alle Firmen") */
  scopeTenantId: string | null
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface QuoteStandardInput {
  category: string
  title: string
  preferredVendor?: string
  minSpecsJson?: string
  defaultServiceItemCodes?: string
  sapPreferredGroupCodes?: string
  sourceType?: 'manual' | 'confluence' | 'excel'
  confluencePageId?: string | null
  confluenceSpaceKey?: string | null
  active?: boolean
}

export interface SyncResult {
  id: string
  category: string
  title: string
  ok: boolean
  error?: string
}

export interface SyncAllSummary {
  data: SyncResult[]
  summary: { total: number; ok: number; failed: number }
}

export async function fetchQuoteStandards(categoryPrefix?: string): Promise<QuoteStandard[]> {
  const params = categoryPrefix ? { categoryPrefix } : {}
  const res = await apiClient.get<{ data: QuoteStandard[] }>('/quote-standards', { params })
  return res.data.data
}

export async function createQuoteStandard(input: QuoteStandardInput): Promise<QuoteStandard> {
  const res = await apiClient.post<{ data: QuoteStandard }>('/quote-standards', parseInput(input))
  return res.data.data
}

export async function updateQuoteStandard(
  id: string,
  input: Partial<QuoteStandardInput>,
): Promise<QuoteStandard> {
  const res = await apiClient.patch<{ data: QuoteStandard }>(
    `/quote-standards/${id}`,
    parseInput(input),
  )
  return res.data.data
}

export async function deleteQuoteStandard(id: string): Promise<void> {
  await apiClient.delete(`/quote-standards/${id}`)
}

export async function syncQuoteStandard(id: string): Promise<SyncResult> {
  const res = await apiClient.post<{ data: SyncResult }>(`/quote-standards/${id}/sync`, {})
  return res.data.data
}

export async function syncAllQuoteStandards(): Promise<SyncAllSummary> {
  const res = await apiClient.post<SyncAllSummary>('/quote-standards/sync-all', {})
  return res.data
}

function parseInput(input: Partial<QuoteStandardInput>): Record<string, unknown> {
  const out: Record<string, unknown> = {}

  if (input.category !== undefined) out.category = input.category
  if (input.title !== undefined) out.title = input.title
  if (input.preferredVendor !== undefined) out.preferredVendor = input.preferredVendor || null
  if (input.active !== undefined) out.active = input.active
  if (input.sourceType !== undefined) out.sourceType = input.sourceType
  if (input.confluencePageId !== undefined) out.confluencePageId = input.confluencePageId || null
  if (input.confluenceSpaceKey !== undefined) out.confluenceSpaceKey = input.confluenceSpaceKey || null

  // JSON text fields: send as parsed objects (backend validates them)
  for (const key of ['minSpecsJson', 'defaultServiceItemCodes', 'sapPreferredGroupCodes'] as const) {
    if (input[key] !== undefined) {
      const raw = (input[key] ?? '').trim()
      if (!raw) {
        out[key] = null
      } else {
        try {
          out[key] = JSON.parse(raw)
        } catch {
          // send raw string so backend returns a proper 422 with field name
          out[key] = raw
        }
      }
    }
  }

  return out
}

// ─── Excel import ─────────────────────────────────────────────────────────────

export interface ExcelSheetPreview {
  name: string
  rowCount: number
  colCount: number
  preview: string[][]
}

export interface ExcelSheetSelection {
  sheetName: string
  category: string
  title: string
  preferredVendor?: string
}

export async function analyzeExcelFile(file: File): Promise<ExcelSheetPreview[]> {
  const form = new FormData()
  form.append('file', file)
  const res = await apiClient.post<{ data: ExcelSheetPreview[] }>(
    '/quote-standards/excel/analyze',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return res.data.data
}

export async function importExcelFile(
  file: File,
  selections: ExcelSheetSelection[],
): Promise<QuoteStandard[]> {
  const form = new FormData()
  form.append('file', file)
  form.append('selections', JSON.stringify(selections))
  const res = await apiClient.post<{ data: QuoteStandard[] }>(
    '/quote-standards/excel/import',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return res.data.data
}
